import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { config } from "../config/env.config";
import { ApiService, apiService, PusherService } from "../services";
import {
  BenchmarkActiveQuestion,
  BenchmarkAnswer,
  BenchmarkEvaluationSaveStatus,
  BenchmarkEvaluationUsage,
  BenchmarkEvaluationVerdict,
  BenchmarkLaneState,
  BenchmarkLaneStatus,
  BenchmarkModelSelection,
  BenchmarkResponseAnnotation,
  BenchmarkResponseDimension,
  BenchmarkResponseVerdict,
  BenchmarkTraceEvent,
  InputOption,
  InterviewQuestionInputType,
  Message,
  MessageRole,
  RequestStates,
  SendMessageRequest,
} from "../types";
import { generateUUID } from "../utils";
import { sortTraceEvents } from "../components/ModelBenchmark/trace-timeline";

interface LaneRuntime {
  api: ApiService;
  abortController: AbortController;
  pusher: PusherService | null;
  startPromise: ReturnType<ApiService["startInterview"]> | null;
  pendingStartedAt: number | null;
  pendingRequest: SendMessageRequest | null;
  responseTimeoutId: ReturnType<typeof setTimeout> | null;
  submissionVersion: number;
  canRetrySubmission: boolean;
  isRetrying: boolean;
}

interface StartBenchmarkOptions {
  interviewLocationId: string;
  selections: BenchmarkModelSelection[];
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Unexpected benchmark error";

const MAX_TRACE_EVENTS_PER_LANE = 500;
const COMPLETED_STATUSES = new Set(["complete", "completed"]);
const PROVIDER_FALLBACK_STATUS_DELAY_MS = 10_000;
const RETRY_RESPONSE_TIMEOUT_MS = 15_000;
const RETRY_TIMEOUT_ERROR = "The retry received no response within 15 seconds.";

type LaneSubmissionMode = "initial" | "manual-retry";

const clearResponseTimeout = (runtime: LaneRuntime) => {
  if (runtime.responseTimeoutId !== null) {
    clearTimeout(runtime.responseTimeoutId);
    runtime.responseTimeoutId = null;
  }
};

function getTurnMetrics(
  events: BenchmarkTraceEvent[],
  turnId: string,
): {
  latencyMs?: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  costUsd: number;
  costCoverage: BenchmarkEvaluationUsage["costCoverage"];
  routedProvider?: string;
} {
  const turnEvents = events.filter((event) => event.turnId === turnId);
  const llmEvents = turnEvents.filter(({ type }) => type === "llm.completed");
  const costSources = new Set(
    llmEvents
      .map(({ costSource }) => costSource)
      .filter((source): source is "provider" | "estimated" => Boolean(source)),
  );
  const completedTurn = turnEvents.find(
    ({ type }) => type === "turn.completed",
  );
  const latestLlmEvent = llmEvents[llmEvents.length - 1];
  const costCoverage =
    costSources.size === 0
      ? "none"
      : costSources.size > 1
        ? "mixed"
        : costSources.has("provider")
          ? "provider"
          : "estimated";

  return {
    ...(completedTurn?.durationMs !== undefined
      ? { latencyMs: completedTurn.durationMs }
      : latestLlmEvent?.durationMs !== undefined
        ? { latencyMs: latestLlmEvent.durationMs }
        : {}),
    inputTokens: llmEvents.reduce(
      (total, event) => total + (event.inputTokens ?? 0),
      0,
    ),
    outputTokens: llmEvents.reduce(
      (total, event) => total + (event.outputTokens ?? 0),
      0,
    ),
    reasoningTokens: llmEvents.reduce(
      (total, event) => total + (event.reasoningTokens ?? 0),
      0,
    ),
    totalTokens: llmEvents.reduce(
      (total, event) => total + (event.totalTokens ?? 0),
      0,
    ),
    costUsd: llmEvents.reduce(
      (total, event) => total + (event.costUsd ?? event.estimatedCostUsd ?? 0),
      0,
    ),
    costCoverage,
    ...(latestLlmEvent?.openRouterProvider
      ? { routedProvider: latestLlmEvent.openRouterProvider }
      : {}),
  };
}

const WRITTEN_INPUT_TYPES = new Set([
  InterviewQuestionInputType.TEXT,
  InterviewQuestionInputType.LONG_TEXT,
  InterviewQuestionInputType.PHONE,
  InterviewQuestionInputType.NUMERICAL,
]);

const CHOICE_INPUT_TYPES = new Set([
  InterviewQuestionInputType.YES_NO,
  InterviewQuestionInputType.SINGLE_CHOICE,
  InterviewQuestionInputType.MULTIPLE_CHOICE,
]);

const DEFAULT_YES_NO_OPTIONS: InputOption[] = [
  { id: "yes", optionText: "Yes", optionValue: "yes", sortOrder: 0 },
  { id: "no", optionText: "No", optionValue: "no", sortOrder: 1 },
];

const getLatestQuestion = (lane: BenchmarkLaneState) =>
  [...lane.messages]
    .reverse()
    .find(({ role }) => role === MessageRole.ASSISTANT);

const normalizeOptionPart = (value: string | undefined) =>
  value?.trim().toLocaleLowerCase() ?? "";

const getOptionKey = ({ optionText, optionValue }: InputOption) =>
  normalizeOptionPart(optionValue) || normalizeOptionPart(optionText);

const getQuestionOptions = (message: Message): InputOption[] => {
  if (message.options?.length) {
    return message.options;
  }

  return message.inputType === InterviewQuestionInputType.YES_NO
    ? DEFAULT_YES_NO_OPTIONS
    : [];
};

const getActiveQuestion = (
  lanes: BenchmarkLaneState[],
): BenchmarkActiveQuestion | null => {
  if (lanes.length === 0) {
    return null;
  }

  if (
    lanes.some(
      ({ status }) =>
        status === BenchmarkLaneStatus.CONNECTING ||
        status === BenchmarkLaneStatus.WAITING,
    )
  ) {
    return null;
  }

  const questions = lanes.map(getLatestQuestion);
  if (questions.some((question) => !question)) {
    return null;
  }

  const presentQuestions = questions as Message[];
  const inputTypes = presentQuestions.map(
    ({ inputType }) => inputType ?? InterviewQuestionInputType.TEXT,
  );
  const inputType = inputTypes[0]!;
  const key = presentQuestions.map(({ id }) => id).join(":");
  const options = getQuestionOptions(presentQuestions[0]!);
  const laneQuestions = presentQuestions.map((question, index) => ({
    laneId: lanes[index]!.id,
    laneLabel: lanes[index]!.label,
    prompt: String(question.content),
    inputType: inputTypes[index]!,
    options: getQuestionOptions(question),
  }));

  const unsupportedQuestion = laneQuestions.find(
    ({ inputType: laneInputType, options: laneOptions }) =>
      (!WRITTEN_INPUT_TYPES.has(laneInputType) &&
        !CHOICE_INPUT_TYPES.has(laneInputType)) ||
      (CHOICE_INPUT_TYPES.has(laneInputType) && laneOptions.length === 0),
  );

  if (unsupportedQuestion) {
    return {
      key,
      inputType,
      options,
      canAnswer: false,
      issue: `${unsupportedQuestion.laneLabel} requires a ${unsupportedQuestion.inputType.replace(/_/g, " ")} response that is not supported in the benchmark composer yet.`,
    };
  }

  if (inputTypes.some((laneInputType) => laneInputType !== inputType)) {
    return {
      key,
      inputType,
      options,
      canAnswer: true,
      laneQuestions,
      requiresLaneAnswers: true,
    };
  }

  if (CHOICE_INPUT_TYPES.has(inputType)) {
    const optionKeys = options.map(getOptionKey);
    const optionsMatch = presentQuestions.every((question) => {
      const laneOptionKeys = getQuestionOptions(question).map(getOptionKey);
      return (
        laneOptionKeys.length === optionKeys.length &&
        optionKeys.every((optionKey) => laneOptionKeys.includes(optionKey))
      );
    });

    if (options.length === 0 || !optionsMatch) {
      return {
        key,
        inputType,
        options,
        canAnswer: true,
        laneQuestions,
        requiresLaneAnswers: true,
      };
    }
  }

  return { key, inputType, options, canAnswer: true };
};

const findLaneOption = (
  selectedOption: InputOption,
  laneOptions: InputOption[],
) => {
  const selectedKey = getOptionKey(selectedOption);
  return laneOptions.find(
    (laneOption) =>
      getOptionKey(laneOption) === selectedKey ||
      laneOption.id === selectedOption.id,
  );
};

export function useModelBenchmark() {
  const [benchmarkId, setBenchmarkId] = useState<string | null>(null);
  const [lanes, setLanes] = useState<BenchmarkLaneState[]>([]);
  const [isGeneratingCandidateResponse, setIsGeneratingCandidateResponse] =
    useState(false);
  const [candidateResponseError, setCandidateResponseError] = useState<
    string | null
  >(null);
  const runtimesRef = useRef<Map<string, LaneRuntime>>(new Map());
  const activeBenchmarkRef = useRef<{
    benchmarkId: string;
    sessionIds: string[];
  } | null>(null);
  const generationAbortControllerRef = useRef<AbortController | null>(null);

  const activeQuestion = useMemo(() => getActiveQuestion(lanes), [lanes]);

  const updateLane = useCallback(
    (
      laneId: string,
      updater: (lane: BenchmarkLaneState) => BenchmarkLaneState,
    ) => {
      setLanes((current) =>
        current.map((lane) => (lane.id === laneId ? updater(lane) : lane)),
      );
    },
    [],
  );

  const disconnect = useCallback((): LaneRuntime[] => {
    const runtimes = Array.from(runtimesRef.current.values());
    runtimes.forEach((runtime) => {
      clearResponseTimeout(runtime);
      runtime.submissionVersion += 1;
      runtime.abortController.abort();
      runtime.pusher?.disconnect();
    });
    runtimesRef.current.clear();
    return runtimes;
  }, []);

  const teardownBenchmark = useCallback(() => {
    const benchmark = activeBenchmarkRef.current;
    activeBenchmarkRef.current = null;
    const runtimes = disconnect();

    generationAbortControllerRef.current?.abort();
    generationAbortControllerRef.current = null;

    if (!benchmark) {
      return;
    }

    const pendingStarts = runtimes
      .map(({ startPromise }) => startPromise)
      .filter((startPromise) => startPromise !== null);
    void Promise.allSettled(pendingStarts)
      .then(() => apiService.cancelBenchmarkSessions(benchmark))
      .catch(() => undefined);
  }, [disconnect]);

  const resetBenchmark = useCallback(() => {
    teardownBenchmark();
    setBenchmarkId(null);
    setLanes([]);
    setIsGeneratingCandidateResponse(false);
    setCandidateResponseError(null);
  }, [teardownBenchmark]);

  const startBenchmark = useCallback(
    async ({ interviewLocationId, selections }: StartBenchmarkOptions) => {
      teardownBenchmark();

      const nextBenchmarkId = generateUUID();
      const nextLanes = selections.map((selection, index) => ({
        ...selection,
        id: String.fromCharCode(65 + index),
        label: `Model ${String.fromCharCode(65 + index)}`,
        sessionId: generateUUID(),
        status: BenchmarkLaneStatus.CONNECTING,
        messages: [],
        traceEvents: [],
        responseAnnotations: {},
      }));

      setBenchmarkId(nextBenchmarkId);
      setLanes(nextLanes);
      activeBenchmarkRef.current = {
        benchmarkId: nextBenchmarkId,
        sessionIds: nextLanes.map(({ sessionId }) => sessionId),
      };

      await Promise.all(
        nextLanes.map(async (lane) => {
          const api = new ApiService();
          const abortController = new AbortController();
          const runtime: LaneRuntime = {
            api,
            abortController,
            pusher: null,
            startPromise: null,
            pendingStartedAt: null,
            pendingRequest: null,
            responseTimeoutId: null,
            submissionVersion: 0,
            canRetrySubmission: false,
            isRetrying: false,
          };

          const pusher = new PusherService(
            config.pusherKey,
            config.pusherCluster,
            `interview-${lane.sessionId}`,
            (message) => {
              if (runtimesRef.current.get(lane.id) !== runtime) {
                return;
              }

              const latency = runtime.pendingStartedAt
                ? Math.round(performance.now() - runtime.pendingStartedAt)
                : undefined;
              clearResponseTimeout(runtime);
              runtime.pendingStartedAt = null;
              runtime.pendingRequest = null;
              runtime.submissionVersion += 1;
              runtime.canRetrySubmission = false;
              runtime.isRetrying = false;
              updateLane(lane.id, (current) => ({
                ...current,
                // A retried turn can deliver both the late original event and
                // the worker's replay with the same message id — append once.
                messages: current.messages.some(({ id }) => id === message.id)
                  ? current.messages
                  : [...current.messages, message],
                status: BenchmarkLaneStatus.READY,
                lastLatencyMs: latency ?? current.lastLatencyMs,
                error: undefined,
                canRetrySubmission: false,
                isAutoRetrying: false,
              }));
            },
            ({ status }) => {
              if (runtimesRef.current.get(lane.id) !== runtime) {
                return;
              }
              if (status && COMPLETED_STATUSES.has(status.toLowerCase())) {
                clearResponseTimeout(runtime);
                runtime.pendingStartedAt = null;
                runtime.pendingRequest = null;
                runtime.submissionVersion += 1;
                runtime.canRetrySubmission = false;
                runtime.isRetrying = false;
                updateLane(lane.id, (current) => ({
                  ...current,
                  status: BenchmarkLaneStatus.COMPLETE,
                  error: undefined,
                  canRetrySubmission: false,
                  isAutoRetrying: false,
                }));
              }
            },
            (error) => {
              if (runtimesRef.current.get(lane.id) !== runtime) {
                return;
              }
              clearResponseTimeout(runtime);
              runtime.pendingStartedAt = null;
              runtime.submissionVersion += 1;
              runtime.isRetrying = false;
              runtime.canRetrySubmission = runtime.pendingRequest !== null;
              updateLane(lane.id, (current) => ({
                ...current,
                status: BenchmarkLaneStatus.ERROR,
                error,
                canRetrySubmission: runtime.canRetrySubmission,
                isAutoRetrying: false,
              }));
            },
            (event: BenchmarkTraceEvent) => {
              if (runtimesRef.current.get(lane.id) !== runtime) {
                return;
              }
              updateLane(lane.id, (current) => ({
                ...current,
                traceEvents: sortTraceEvents([
                  ...(current.traceEvents ?? []),
                  event,
                ]).slice(-MAX_TRACE_EVENTS_PER_LANE),
              }));
            },
          );

          runtime.pusher = pusher;
          runtimesRef.current.set(lane.id, runtime);

          try {
            await pusher.connect();
            if (runtimesRef.current.get(lane.id) !== runtime) {
              pusher.disconnect();
              return;
            }
            runtime.pendingStartedAt = performance.now();
            runtime.startPromise = api.startInterview({
              interviewLocationId,
              sessionId: lane.sessionId,
              metadata: {
                source: "local-model-benchmark",
                localModelBenchmark: {
                  benchmarkId: nextBenchmarkId,
                  laneId: lane.id,
                  provider: lane.provider,
                  model: lane.model,
                },
              },
            });
            await runtime.startPromise;

            if (runtimesRef.current.get(lane.id) !== runtime) {
              return;
            }

            updateLane(lane.id, (current) => ({
              ...current,
              status:
                current.messages.length > 0
                  ? current.status
                  : BenchmarkLaneStatus.WAITING,
            }));
          } catch (error) {
            runtime.pendingStartedAt = null;
            pusher.disconnect();
            if (runtimesRef.current.get(lane.id) !== runtime) {
              return;
            }
            runtimesRef.current.delete(lane.id);
            updateLane(lane.id, (current) => ({
              ...current,
              status: BenchmarkLaneStatus.ERROR,
              error: getErrorMessage(error),
              canRetrySubmission: false,
            }));
          }
        }),
      );
    },
    [teardownBenchmark, updateLane],
  );

  const submitLaneRequest = useCallback(
    async (
      laneId: string,
      request: SendMessageRequest,
      mode: LaneSubmissionMode = "initial",
    ) => {
      const runtime = runtimesRef.current.get(laneId);
      if (!runtime) {
        return;
      }

      const submit = async (submissionMode: LaneSubmissionMode) => {
        clearResponseTimeout(runtime);
        runtime.pendingRequest = request;
        runtime.pendingStartedAt = performance.now();
        runtime.canRetrySubmission = false;
        const submissionVersion = ++runtime.submissionVersion;

        const markRetryTimedOut = () => {
          runtime.responseTimeoutId = null;
          if (
            runtimesRef.current.get(laneId) !== runtime ||
            runtime.submissionVersion !== submissionVersion ||
            runtime.pendingRequest !== request
          ) {
            return;
          }

          runtime.pendingStartedAt = null;
          runtime.canRetrySubmission = true;
          runtime.isRetrying = false;
          updateLane(laneId, (current) => ({
            ...current,
            status: BenchmarkLaneStatus.ERROR,
            error: RETRY_TIMEOUT_ERROR,
            canRetrySubmission: true,
            isAutoRetrying: false,
          }));
        };

        try {
          await runtime.api.sendMessage(
            request,
            runtime.abortController.signal,
          );

          if (
            runtimesRef.current.get(laneId) !== runtime ||
            runtime.submissionVersion !== submissionVersion ||
            runtime.pendingRequest !== request
          ) {
            return;
          }

          if (submissionMode === "initial") {
            runtime.responseTimeoutId = setTimeout(() => {
              runtime.responseTimeoutId = null;
              if (
                runtimesRef.current.get(laneId) !== runtime ||
                runtime.submissionVersion !== submissionVersion ||
                runtime.pendingRequest !== request
              ) {
                return;
              }

              runtime.isRetrying = true;
              updateLane(laneId, (current) => ({
                ...current,
                status: BenchmarkLaneStatus.WAITING,
                error: undefined,
                canRetrySubmission: false,
                isAutoRetrying: true,
              }));
              runtime.responseTimeoutId = setTimeout(
                markRetryTimedOut,
                RETRY_RESPONSE_TIMEOUT_MS,
              );
            }, PROVIDER_FALLBACK_STATUS_DELAY_MS);
          } else {
            runtime.responseTimeoutId = setTimeout(
              markRetryTimedOut,
              RETRY_RESPONSE_TIMEOUT_MS,
            );
          }
        } catch (error) {
          if (
            runtimesRef.current.get(laneId) !== runtime ||
            runtime.submissionVersion !== submissionVersion ||
            runtime.pendingRequest !== request
          ) {
            return;
          }

          clearResponseTimeout(runtime);
          runtime.pendingStartedAt = null;
          runtime.canRetrySubmission = true;
          runtime.isRetrying = false;
          updateLane(laneId, (current) => ({
            ...current,
            status: BenchmarkLaneStatus.ERROR,
            error: getErrorMessage(error),
            canRetrySubmission: true,
            isAutoRetrying: false,
          }));
        }
      };

      await submit(mode);
    },
    [updateLane],
  );

  const submitAnswers = useCallback(
    async (answersByLane: Record<string, BenchmarkAnswer>) => {
      const laneRequests = lanes.map((lane) => {
        const question = getLatestQuestion(lane);
        const answer = answersByLane[lane.id];
        const trimmedContent = answer?.content.trim();
        const selectedOptions = answer?.selectedOptions ?? [];
        const laneInputType =
          question?.inputType ?? InterviewQuestionInputType.TEXT;
        if (
          !question ||
          !trimmedContent ||
          (CHOICE_INPUT_TYPES.has(laneInputType) &&
            selectedOptions.length === 0)
        ) {
          return null;
        }

        const laneOptions = getQuestionOptions(question);
        const matchedOptions = selectedOptions.map((selectedOption) =>
          findLaneOption(selectedOption, laneOptions),
        );
        if (
          !matchedOptions.every(
            (option): option is InputOption => option !== undefined,
          )
        ) {
          return null;
        }

        return {
          content: trimmedContent,
          lane,
          request: {
            sessionId: lane.sessionId,
            messageId: generateUUID(),
            content: trimmedContent,
            ...(matchedOptions.length
              ? {
                  selectedOptionIds: matchedOptions.map(({ id }) => id),
                }
              : {}),
            ...(question.questionId
              ? { interviewQuestionId: question.questionId }
              : {}),
            ...(question.questionType
              ? { questionType: question.questionType }
              : {}),
            responseFormat: matchedOptions.length ? "choice" : "written",
          } satisfies SendMessageRequest,
        };
      });

      if (laneRequests.some((laneRequest) => !laneRequest)) {
        return;
      }

      const resolvedLaneRequests = laneRequests.filter(
        (laneRequest): laneRequest is NonNullable<typeof laneRequest> =>
          laneRequest !== null,
      );
      const responseContentByLane = new Map(
        resolvedLaneRequests.map(({ content, lane }) => [lane.id, content]),
      );
      const sentAt = new Date();
      setLanes((current) =>
        current.map((lane) => {
          const content = responseContentByLane.get(lane.id);
          if (!content) {
            return lane;
          }

          return {
            ...lane,
            messages: [
              ...lane.messages,
              {
                id: generateUUID(),
                role: MessageRole.USER,
                content,
                timestamp: sentAt,
                status: RequestStates.Success,
              } satisfies Message,
            ],
            status: BenchmarkLaneStatus.WAITING,
            error: undefined,
            canRetrySubmission: false,
            isAutoRetrying: false,
          };
        }),
      );

      await Promise.all(
        resolvedLaneRequests.map((laneRequest) => {
          const { lane, request } = laneRequest;
          return submitLaneRequest(lane.id, request);
        }),
      );
    },
    [lanes, submitLaneRequest],
  );

  const sendToAll = useCallback(
    async (answer: BenchmarkAnswer) => {
      if (!activeQuestion?.canAnswer || activeQuestion.requiresLaneAnswers) {
        return;
      }

      const answersByLane = Object.fromEntries(
        lanes.map((lane) => [lane.id, answer]),
      );
      await submitAnswers(answersByLane);
    },
    [activeQuestion, lanes, submitAnswers],
  );

  const sendLaneAnswers = useCallback(
    async (answersByLane: Record<string, BenchmarkAnswer>) => {
      if (!activeQuestion?.canAnswer || !activeQuestion.requiresLaneAnswers) {
        return;
      }

      await submitAnswers(answersByLane);
    },
    [activeQuestion, submitAnswers],
  );

  const retryLane = useCallback(
    async (laneId: string) => {
      const runtime = runtimesRef.current.get(laneId);
      if (
        !runtime?.pendingRequest ||
        !runtime.canRetrySubmission ||
        runtime.isRetrying
      ) {
        return;
      }

      const request = {
        ...runtime.pendingRequest,
        benchmarkRetryId: generateUUID(),
      } satisfies SendMessageRequest;
      runtime.isRetrying = true;
      runtime.canRetrySubmission = false;
      updateLane(laneId, (current) => ({
        ...current,
        status: BenchmarkLaneStatus.WAITING,
        error: undefined,
        canRetrySubmission: false,
        isAutoRetrying: false,
      }));

      try {
        await submitLaneRequest(laneId, request, "manual-retry");
      } finally {
        if (runtimesRef.current.get(laneId) === runtime) {
          runtime.isRetrying = false;
        }
      }
    },
    [submitLaneRequest, updateLane],
  );

  const saveLaneEvaluation = useCallback(
    async (
      laneId: string,
      verdict: BenchmarkEvaluationVerdict,
      usage: BenchmarkEvaluationUsage,
    ) => {
      const runtime = runtimesRef.current.get(laneId);
      const lane = lanes.find(({ id }) => id === laneId);
      if (
        !benchmarkId ||
        !runtime ||
        !lane ||
        lane.evaluation?.status === BenchmarkEvaluationSaveStatus.SAVING
      ) {
        return;
      }

      updateLane(laneId, (current) => ({
        ...current,
        evaluation: {
          verdict,
          status: BenchmarkEvaluationSaveStatus.SAVING,
        },
      }));

      try {
        const [session, transcript] = await Promise.all([
          runtime.api.getSession(
            lane.sessionId,
            runtime.abortController.signal,
          ),
          runtime.api.getSessionMessages(
            lane.sessionId,
            runtime.abortController.signal,
          ),
        ]);
        const result = await runtime.api.saveBenchmarkEvaluation(
          {
            benchmarkId,
            laneId: lane.id,
            sessionId: lane.sessionId,
            verdict,
            provider: lane.provider,
            model: lane.model,
            turnCount: transcript.filter(
              ({ role }) => role === MessageRole.USER,
            ).length,
            messageCount: transcript.length,
            transcript,
            stateSnapshot: {
              sessionStatus: session.status,
              orchestratorState: session.orchestratorState,
              sessionMetadata: session.sessionMetadata,
              startedAt: session.startedAt,
              completedAt: session.completedAt,
              laneStatus: lane.status,
              ...(lane.lastLatencyMs !== undefined
                ? { lastLatencyMs: lane.lastLatencyMs }
                : {}),
              ...(lane.error ? { laneError: lane.error } : {}),
              traceEvents: lane.traceEvents ?? [],
            },
            usage,
          },
          runtime.abortController.signal,
        );

        updateLane(laneId, (current) => ({
          ...current,
          evaluation: {
            verdict,
            status: BenchmarkEvaluationSaveStatus.SAVED,
            resultId: result.id,
            evaluatedAt: result.evaluatedAt,
          },
        }));
      } catch (error) {
        updateLane(laneId, (current) => ({
          ...current,
          evaluation: {
            verdict,
            status: BenchmarkEvaluationSaveStatus.ERROR,
            error: getErrorMessage(error),
          },
        }));
      }
    },
    [benchmarkId, lanes, updateLane],
  );

  const saveResponseAnnotation = useCallback(
    async (
      laneId: string,
      messageId: string,
      annotation: {
        verdict: BenchmarkResponseVerdict;
        dimension: BenchmarkResponseDimension;
        reason: string;
      },
    ): Promise<BenchmarkResponseAnnotation> => {
      const runtime = runtimesRef.current.get(laneId);
      const lane = lanes.find(({ id }) => id === laneId);
      const messageIndex = lane?.messages.findIndex(
        ({ id }) => id === messageId,
      );
      const response =
        lane && messageIndex !== undefined && messageIndex >= 0
          ? lane.messages[messageIndex]
          : undefined;
      if (!benchmarkId || !runtime || !lane || !response) {
        throw new Error("Benchmark response is no longer available");
      }
      if (!response.benchmarkTurnId) {
        throw new Error("This response has no benchmark trace correlation");
      }

      const conversationPrefix = lane.messages.slice(0, messageIndex);
      const candidateMessage = [...conversationPrefix]
        .reverse()
        .find(({ role }) => role === MessageRole.USER);
      const candidateIndex = candidateMessage
        ? conversationPrefix.lastIndexOf(candidateMessage)
        : -1;
      const questionMessage =
        candidateIndex > 0
          ? [...conversationPrefix.slice(0, candidateIndex)]
              .reverse()
              .find(({ role }) => role === MessageRole.ASSISTANT)
          : undefined;
      const turnMetrics = getTurnMetrics(
        lane.traceEvents ?? [],
        response.benchmarkTurnId,
      );
      const responseState = lane.traceEvents?.find(
        ({ turnId, type }) =>
          turnId === response.benchmarkTurnId && type === "turn.completed",
      )?.stateChanges;
      const session = await runtime.api.getSession(
        lane.sessionId,
        runtime.abortController.signal,
      );
      const saved = await runtime.api.saveBenchmarkResponseAnnotation(
        {
          benchmarkId,
          laneId,
          sessionId: lane.sessionId,
          turnId: response.benchmarkTurnId,
          messageId: response.id,
          ...(lane.responseAnnotations?.[response.id]
            ? {
                replacesAnnotationId: lane.responseAnnotations[response.id]!.id,
              }
            : {}),
          ...annotation,
          provider: lane.provider,
          model: lane.model,
          ...(turnMetrics.routedProvider
            ? { routedProvider: turnMetrics.routedProvider }
            : {}),
          response: String(response.content),
          question: {
            ...(questionMessage?.questionId
              ? { id: questionMessage.questionId }
              : {}),
            ...(questionMessage?.content
              ? { content: String(questionMessage.content) }
              : {}),
            ...(questionMessage?.inputType
              ? { inputType: questionMessage.inputType }
              : {}),
            ...(questionMessage?.options
              ? {
                  options: questionMessage.options.map(
                    ({ optionText }) => optionText,
                  ),
                }
              : {}),
          },
          ...(candidateMessage?.content
            ? { candidateResponse: String(candidateMessage.content) }
            : {}),
          conversationPrefix: conversationPrefix.map((message) => ({
            id: message.id,
            role: message.role,
            content: String(message.content),
            ...(message.questionId ? { questionId: message.questionId } : {}),
            ...(message.inputType ? { inputType: message.inputType } : {}),
          })),
          stateSnapshot: {
            ...(responseState ? { responseState } : {}),
            sessionStatus: session.status,
            orchestratorState: session.orchestratorState,
            sessionMetadata: session.sessionMetadata,
          },
          metrics: {
            ...(turnMetrics.latencyMs !== undefined
              ? { latencyMs: turnMetrics.latencyMs }
              : {}),
            inputTokens: turnMetrics.inputTokens,
            outputTokens: turnMetrics.outputTokens,
            reasoningTokens: turnMetrics.reasoningTokens,
            totalTokens: turnMetrics.totalTokens,
            costUsd: turnMetrics.costUsd,
            costCoverage: turnMetrics.costCoverage,
          },
        },
        runtime.abortController.signal,
      );

      updateLane(laneId, (current) => ({
        ...current,
        responseAnnotations: {
          ...(current.responseAnnotations ?? {}),
          [response.id]: saved,
        },
      }));
      return saved;
    },
    [benchmarkId, lanes, updateLane],
  );

  const currentQuestions = useMemo(
    () =>
      Array.from(
        new Set(
          lanes
            .map((lane) =>
              [...lane.messages]
                .reverse()
                .find(({ role }) => role === MessageRole.ASSISTANT),
            )
            .filter((message): message is Message => Boolean(message))
            .map(({ content }) => String(content).trim())
            .filter(Boolean),
        ),
      ),
    [lanes],
  );

  const generateCandidateResponse = useCallback(
    async (options?: { laneId?: string; questions?: string[] }) => {
      const questions = options?.questions ?? currentQuestions;
      if (questions.length === 0) {
        setCandidateResponseError("Wait for an interviewer question first.");
        return null;
      }

      generationAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      generationAbortControllerRef.current = abortController;
      setIsGeneratingCandidateResponse(true);
      setCandidateResponseError(null);
      try {
        const sourceLane = options?.laneId
          ? lanes.find(({ id }) => id === options.laneId)
          : lanes[0];
        const priorCandidateResponses =
          sourceLane?.messages
            .filter(({ role }) => role === MessageRole.USER)
            .map(({ content }) => String(content)) ?? [];
        const response = await apiService.generateBenchmarkCandidateResponse(
          {
            questions,
            priorCandidateResponses,
          },
          abortController.signal,
        );
        return response.content;
      } catch (error) {
        if (abortController.signal.aborted) {
          return null;
        }
        setCandidateResponseError(getErrorMessage(error));
        return null;
      } finally {
        if (generationAbortControllerRef.current === abortController) {
          generationAbortControllerRef.current = null;
          setIsGeneratingCandidateResponse(false);
        }
      }
    },
    [currentQuestions, lanes],
  );

  useEffect(() => () => teardownBenchmark(), [teardownBenchmark]);

  const canSend = useMemo(
    () =>
      lanes.length > 0 &&
      lanes.every((lane) => lane.status === BenchmarkLaneStatus.READY) &&
      activeQuestion?.canAnswer === true,
    [activeQuestion, lanes],
  );
  const canGenerateCandidateResponse =
    canSend &&
    currentQuestions.length > 0 &&
    Boolean(
      activeQuestion &&
      (activeQuestion.requiresLaneAnswers
        ? activeQuestion.laneQuestions?.filter(({ inputType }) =>
            WRITTEN_INPUT_TYPES.has(inputType),
          ).length === 1
        : WRITTEN_INPUT_TYPES.has(activeQuestion.inputType)),
    );

  return {
    benchmarkId,
    lanes,
    activeQuestion,
    canSend,
    canGenerateCandidateResponse,
    isGeneratingCandidateResponse,
    candidateResponseError,
    startBenchmark,
    sendToAll,
    sendLaneAnswers,
    retryLane,
    saveLaneEvaluation,
    saveResponseAnnotation,
    generateCandidateResponse,
    resetBenchmark,
  };
}
